import { cx } from "@/utils/cx";

export interface ProgressBarProps {
	/**
	 * Optional additional CSS class names for the progress bar container.
	 */
	className?: string;
	/**
	 * The maximum value of the progress bar.
	 * @default 100
	 */
	max?: number;
	/**
	 * The minimum value of the progress bar.
	 * @default 0
	 */
	min?: number;
	/**
	 * Optional additional CSS class names for the progress bar indicator element.
	 */
	progressClassName?: string;
	/**
	 * The current value of the progress bar.
	 */
	value: number;
	/**
	 * Optional function to format the displayed value.
	 * It receives the raw value and the calculated percentage.
	 */
	valueFormatter?: (
		value: number,
		valueInPercentage: number,
	) => number | string;
}

/**
 * A basic progress bar component.
 */
export const ProgressBarBase = ({
	className,
	max = 100,
	min = 0,
	progressClassName,
	value,
}: ProgressBarProps) => {
	const percentage = ((value - min) * 100) / (max - min);

	return (
		<div
			aria-valuemax={max}
			aria-valuemin={min}
			aria-valuenow={value}
			className={cx(
				"h-2 w-full overflow-hidden rounded-md bg-quaternary",
				className,
			)}
			role="progressbar"
		>
			<div
				className={cx(
					"size-full rounded-md bg-fg-brand-primary transition duration-75 ease-linear",
					progressClassName,
				)}
				// Use transform instead of width to avoid layout thrashing (and for smoother animation)
				style={{ transform: `translateX(-${100 - percentage}%)` }}
			/>
		</div>
	);
};

export interface ProgressIndicatorWithTextProps extends ProgressBarProps {
	/**
	 * Specifies the layout of the text relative to the progress bar.
	 * - `right`: Text is displayed to the right of the progress bar.
	 * - `bottom`: Text is displayed below the progress bar, aligned to the right.
	 * - `top-floating`: Text is displayed in a floating tooltip above the progress indicator.
	 * - `bottom-floating`: Text is displayed in a floating tooltip below the progress indicator.
	 */
	labelPosition?: ProgressBarLabelPosition;
}

type ProgressBarLabelPosition =
	| "bottom"
	| "bottom-floating"
	| "right"
	| "top-floating";

/**
 * A progress bar component that displays the value text in various configurable layouts.
 */
export const ProgressBar = ({
	className,
	labelPosition,
	max = 100,
	min = 0,
	progressClassName,
	value,
	valueFormatter,
}: ProgressIndicatorWithTextProps) => {
	const percentage = ((value - min) * 100) / (max - min);
	const formattedValue = valueFormatter
		? valueFormatter(value, percentage)
		: `${percentage.toFixed(0)}%`; // Default to rounded percentage

	const baseProgressBar = (
		<ProgressBarBase
			className={className}
			max={max}
			min={min}
			progressClassName={progressClassName}
			value={value}
		/>
	);

	switch (labelPosition) {
		case "bottom":
			return (
				<div className="flex flex-col items-end gap-2">
					{baseProgressBar}
					<span className="text-sm font-medium text-secondary tabular-nums">
						{formattedValue}
					</span>
				</div>
			);
		case "bottom-floating":
			return (
				<div className="relative flex flex-col items-end gap-2">
					{baseProgressBar}
					<div
						className="absolute -bottom-2 -translate-x-1/2 translate-y-full rounded-lg bg-primary_alt px-3 py-2 shadow-lg ring-1 ring-secondary_alt"
						style={{ left: `${percentage}%` }}
					>
						<div className="text-xs font-semibold text-secondary">
							{formattedValue}
						</div>
					</div>
				</div>
			);
		case "right":
			return (
				<div className="flex items-center gap-3">
					{baseProgressBar}
					<span className="shrink-0 text-sm font-medium text-secondary tabular-nums">
						{formattedValue}
					</span>
				</div>
			);
		case "top-floating":
			return (
				<div className="relative flex flex-col items-end gap-2">
					{baseProgressBar}
					<div
						className="absolute -top-2 -translate-x-1/2 -translate-y-full rounded-lg bg-primary_alt px-3 py-2 shadow-lg ring-1 ring-secondary_alt"
						style={{ left: `${percentage}%` }}
					>
						<div className="text-xs font-semibold text-secondary tabular-nums">
							{formattedValue}
						</div>
					</div>
				</div>
			);
		default:
			// Fallback or default case, could render the basic progress bar or throw an error
			return baseProgressBar;
	}
};
